import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge, money, EmptyState } from "../../components/dashboard/Shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const KYC = ["pending", "approved", "rejected"];
const AGR = ["pending", "signed"];

export default function Clients() {
  const [clients, setClients] = useState(null);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // {mode:'add'} | {mode:'edit', client}
  const [form, setForm] = useState({});
  const [tempPass, setTempPass] = useState(null);

  const load = useCallback(() => {
    api.get(`/manager/clients?search=${encodeURIComponent(search)}`).then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm({ name: "", email: "", phone: "", country: "", kyc_status: "pending", agreement_status: "pending" }); setTempPass(null); setModal({ mode: "add" }); };
  const openEdit = (c) => { setForm({ name: c.name, phone: c.phone || "", country: c.country || "", kyc_status: c.kyc_status, agreement_status: c.agreement_status }); setModal({ mode: "edit", client: c }); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (modal.mode === "add") {
        const { data } = await api.post("/manager/clients", form);
        setTempPass(data.temp_password);
        toast.success("Client created");
      } else {
        await api.put(`/manager/clients/${modal.client.user_id}`, form);
        toast.success("Client updated");
        setModal(null);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}? This deletes their account and MT5 link.`)) return;
    await api.delete(`/manager/clients/${c.user_id}`);
    toast.success("Client removed");
    load();
  };

  return (
    <div>
      <PageHeader title="Clients" sub="Onboarding, KYC and account status">
        <button data-testid="add-client-button" onClick={openAdd} className="btn-primary px-4 py-2 text-sm flex items-center gap-2"><Plus size={15} /> Add Client</button>
      </PageHeader>
      <div className="relative max-w-sm mb-5">
        <Search size={15} className="absolute left-3 top-3 text-gray-500" />
        <input data-testid="client-search-input" className="input-dark pl-9" placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {!clients ? <div className="skeleton h-96" /> : clients.length === 0 ? (
        <EmptyState title="No clients found" sub="Add your first client to begin onboarding." />
      ) : (
        <Table headers={["Client", "KYC", "Agreement", "Account", "Equity", "Status", ""]} testId="clients-table">
          {clients.map((c) => (
            <tr key={c.user_id} className="border-b border-white/5 hover:bg-white/[0.02]">
              <Td><p className="text-white">{c.name}</p><p className="text-xs text-gray-500">{c.email}</p></Td>
              <Td><StatusBadge status={c.kyc_status} /></Td>
              <Td><StatusBadge status={c.agreement_status} /></Td>
              <Td className="tabular text-xs">{c.account ? `${c.account.broker} · ${c.account.login}` : "Not connected"}</Td>
              <Td className="tabular">{c.account ? money(c.account.equity) : "—"}</Td>
              <Td><StatusBadge status={c.status} /></Td>
              <Td>
                <div className="flex gap-2">
                  <button data-testid={`edit-client-${c.user_id}`} onClick={() => openEdit(c)} className="text-gray-400 hover:text-[#00F0FF]"><Pencil size={15} /></button>
                  <button data-testid={`delete-client-${c.user_id}`} onClick={() => remove(c)} className="text-gray-400 hover:text-[#FF3B30]"><Trash2 size={15} /></button>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-[#121212] border border-white/10 text-gray-200 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-white">{modal?.mode === "add" ? "Add Client" : "Edit Client"}</DialogTitle></DialogHeader>
          {tempPass ? (
            <div data-testid="temp-password-box" className="space-y-4">
              <p className="text-sm text-gray-400">Share these one-time credentials with your client:</p>
              <div className="card-dark p-4 text-sm">
                <p className="tabular text-white">{form.email}</p>
                <p className="tabular text-[#00F0FF] mt-1">{tempPass}</p>
              </div>
              <button className="btn-primary w-full py-2 text-sm" onClick={() => setModal(null)}>Done</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input data-testid="client-name-input" required minLength={2} className="input-dark" placeholder="Full name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              {modal?.mode === "add" && (
                <input data-testid="client-email-input" required type="email" className="input-dark" placeholder="Email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              )}
              <input data-testid="client-phone-input" className="input-dark" placeholder="Phone (optional)" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input data-testid="client-country-input" className="input-dark" placeholder="Country (optional)" value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-xs block mb-1">KYC Status</label>
                  <select data-testid="client-kyc-select" className="input-dark" value={form.kyc_status} onChange={(e) => setForm({ ...form, kyc_status: e.target.value })}>
                    {KYC.map((k) => <option key={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-xs block mb-1">Agreement</label>
                  <select data-testid="client-agreement-select" className="input-dark" value={form.agreement_status} onChange={(e) => setForm({ ...form, agreement_status: e.target.value })}>
                    {AGR.map((k) => <option key={k}>{k}</option>)}
                  </select>
                </div>
              </div>
              <button data-testid="client-submit-button" className="btn-primary w-full py-2.5 text-sm">{modal?.mode === "add" ? "Create Client" : "Save Changes"}</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
